export const routePatterns = {
  base: "/base/:chartId",
  open: "/open/*",
  plan: "/plan/:planId",
  boxlayout: "/boxlayout/:layoutKey",
  chartSettings: "/settings/chart/:id?/:settingKey?",
  accountSettings: "/settings/account/:settingKey?/:id?",
  planSettings: "/settings/plan/:id?/:settingKey?",
  details: "/details/:detailsKey?"
} as const;

export const routePaths = {
  base: (chartId: string) => `/base/${enc(chartId)}`,
  open: (directory: string, file: string) => `/open/${enc(directory)}/${enc(file)}`,
  plan: (planId: string | number) => `/plan/${enc(String(planId))}`,
  boxlayout: (set: string, id: string) => `/boxlayout/${enc(set)}-${enc(id)}`,
  chartSettings: (chartId?: string) =>
    chartId ? `/settings/chart/${enc(chartId)}` : "/settings/chart",
  accountSettings: () => "/settings/account",
  planSettings: (planId?: string) => (planId ? `/settings/plan/${enc(planId)}` : "/settings/plan"),
  details: (section?: string) => (section ? `/details/${enc(section)}` : "/details")
} as const;

function enc(value: string): string {
  return encodeURIComponent(value);
}

export type ChartMode = "chart" | "edit" | "directory" | "reports" | "view" | "background";
export type SettingsTab =
  | "Presentation"
  | "DataSources"
  | "Mapping"
  | "SearchAndDirectory"
  | "Profile"
  | "FieldsAndFormulas"
  | "FiltersAndHighlights"
  | "Subcharts"
  | "Refresh"
  | "PointInTime";
export type AccountSettingsTab =
  | "General"
  | "BaseCharts"
  | "Theme"
  | "AccessGroups"
  | "ManageUsers"
  | "UIProfiles"
  | "Security"
  | "Authorization"
  | "Checkpoints";
export type SidebarTool = "filters" | "highlights" | "background" | "planning" | "dashboard";

export type ChartQueryParams = {
  mode?: ChartMode;
  tool?: SidebarTool;
  view?: string;
  background?: string;
  topOfChart?: string;
  chartexplorer?: boolean;
  create?: boolean;
  display?: string;
  levels?: string;
  zoom?: string;
};

/** Runtime list of ChartQueryParams keys for iteration (e.g. preserving
 *  canvas context across settings navigation). Single source of truth —
 *  adding a new canvas param here automatically carries it through. */
export const CHART_QUERY_PARAM_KEYS: Array<keyof ChartQueryParams> = [
  "mode",
  "tool",
  "view",
  "background",
  "topOfChart",
  "chartexplorer",
  "create",
  "display",
  "levels",
  "zoom"
];

/** Params that survive cross-route navigation to non-canvas routes **/
export const NON_CANVAS_CARRY_KEYS: Array<keyof ChartQueryParams> = [
  "mode",
  "view",
  "background",
  "topOfChart",
  "create",
  "zoom",
  "levels"
];

export type SettingsQueryParams = {
  tab?: SettingsTab;
};

export type AccountSettingsQueryParams = {
  tab?: AccountSettingsTab;
};

/**
 * Converts a full background file path to the URL-safe FileName param.
 * /Masters/Name.ocb → "Name", /Masters/Legacy.oc5m → "Legacy.oc5m", /Foo/Bar.ocb → "/Foo/Bar"
 */
export function toBackgroundParam(fullPath: string): string {
  const mastersOcb = /^\/Masters\/(.+)\.ocb$/;
  const mastersOther = /^\/Masters\/(.+)$/;
  const otherOcb = /^\/(.+)\.ocb$/;

  let match;
  if ((match = mastersOcb.exec(fullPath))) return match[1];
  if ((match = mastersOther.exec(fullPath))) return match[1];
  if ((match = otherOcb.exec(fullPath))) return `/${match[1]}`;
  return fullPath;
}
